import { ApiClient, HttpError } from './client';

/**
 * Shopify API Client
 * Uses modern App Bridge (v4+) global shopify variable for session tokens
 */
export class ShopifyApiClient extends ApiClient {
  constructor(baseURL: string = '') {
    super(baseURL || import.meta.env.VITE_API_URL || '');
  }

  /**
   * Resolve App Bridge runtime configuration for the current shop context.
   * This endpoint is unauthenticated because App Bridge apiKey is public.
   *
   * When the connection is inactive (pre-created but not yet activated), the
   * response includes `connectionActive: false`. The frontend should initialize
   * App Bridge with the apiKey and then call `activateConnection` with a session
   * token to complete activation via Token Exchange.
   */
  async getAppBridgeConfig(shop?: string): Promise<{
    apiKey: string;
    shop?: string;
    connectionActive?: boolean;
  }> {
    const params = new URLSearchParams();
    if (shop) {
      params.set('shop', shop);
    }

    const endpoint = params.toString().length > 0
      ? `/api-bf/shopify/app-bridge-config?${params.toString()}`
      : '/api-bf/shopify/app-bridge-config';

    const response = await this.get<{
      apiKey: string;
      shop?: string;
      connectionActive?: boolean;
    }>(endpoint);

    return response.data;
  }

  /**
   * Activate a shop connection using Shopify Token Exchange.
   *
   * Called after App Bridge initialization when `getAppBridgeConfig` returns
   * `connectionActive: false`. The session token (id_token) obtained from
   * App Bridge is sent in the Authorization header; the backend validates the
   * JWT, calls Shopify's Token Exchange API to obtain an offline access token,
   * and activates the pre-created connection.
   *
   * This replaces the OAuth authorization code grant flow for embedded apps,
   * avoiding redirect loops and X-Frame-Options errors inside iframes.
   */
  async activateConnection(idToken: string, shop: string): Promise<{
    success: boolean;
    shop: string;
    vendorId?: number;
    connectionActive: boolean;
    message: string;
  }> {
    const response = await this.post<{
      success: boolean;
      shop: string;
      vendorId?: number;
      connectionActive: boolean;
      message: string;
    }>(
      '/api-bf/shopify/activate-connection',
      { shop }, // shop is required - used as primary lookup key to prevent enumeration
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
    return response.data;
  }

  /**
   * Validate JWT token format (basic structure check)
   * @param token - JWT token string
   * @returns true if token has valid JWT structure (3 parts separated by dots)
   */
  private isValidJwtFormat(token: string): boolean {
    if (!token || typeof token !== 'string') {
      return false;
    }
    const parts = token.split('.');
    return parts.length === 3 && parts.every(part => part.length > 0);
  }

  /**
   * Get Shopify session token from global shopify object
   * The modern App Bridge provides a global shopify.idToken() method
   * @throws Error if App Bridge is unavailable or token retrieval fails
   */
  private async getShopifyAuthHeader(): Promise<Record<string, string>> {
    if (typeof window === 'undefined' || !window.shopify) {
      throw new Error('Shopify App Bridge is not available. Ensure the app is loaded within Shopify Admin.');
    }

    try {
      const token = await window.shopify.idToken();
      if (!token) {
        throw new Error('Failed to retrieve session token from Shopify App Bridge.');
      }
      return {
        Authorization: `Bearer ${token}`,
      };
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Failed to retrieve Shopify session token.');
    }
  }

  /**
   * Validate Shopify session and check vendor connection.
   * Sends the Shopify session token in the Authorization header (not in the
   * request body) to avoid WAF inspection of JWT payloads.
   * The shop domain is derived server-side from the token's `dest` claim.
   */
  async validateSession(idToken: string): Promise<{
    valid: boolean;
    shop: string;
    isConnected: boolean;
    vendorId?: number;
    /** True when vendorId is set but the marketplace vendor account is not yet active (pending admin approval). */
    isPending?: boolean;
  }> {
    const response = await this.post<{
      valid: boolean;
      shop: string;
      isConnected: boolean;
      vendorId?: number;
      isPending?: boolean;
    }>(
      '/api-bf/shopify/validate-session',
      {}, // no body needed - shop extracted from token server-side
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
    return response.data;
  }

  /**
   * Get dashboard data for connected vendor
   */
  async getDashboard(vendorId: number): Promise<{
    vendorId: number;
    shopDomain: string;
    connectionStatus: string;
    publishedProductCount: number;
    vendorName: string;
    vendorSeName: string;
    lastSyncedAtUtc?: string | null;
  }> {
    const headers = await this.getShopifyAuthHeader();
    const response = await this.get<{
      vendorId: number;
      shopDomain: string;
      connectionStatus: string;
      publishedProductCount: number;
      vendorName: string;
      vendorSeName: string;
      lastSyncedAtUtc?: string | null;
    }>(`/api-bf/shopify/dashboard/${vendorId}`, {
      headers,
    });
    return response.data;
  }

  /**
   * Disconnect vendor's Shopify store from the marketplace (E.3.17).
   * Unlinks the shop connection without uninstalling the app from Shopify.
   *
   * Expected HTTP error responses from the server:
   *  401 - Shopify session token is missing or invalid (App Bridge unavailable).
   *  403 - The authenticated shop does not match the vendor's linked shop.
   *  404 - No active Shopify connection exists for this vendor.
   *  500 - Unexpected server error.
   *
   * All non-2xx responses are translated into plain Error instances whose `.message`
   * is safe to show directly in the UI.
   */
  async disconnect(vendorId: number): Promise<{ success: boolean; message: string }> {
    const headers = await this.getShopifyAuthHeader();
    try {
      const response = await this.post<{ success: boolean; message: string }>(
        `/api-bf/shopify/disconnect/${vendorId}`,
        {},
        { headers }
      );
      return response.data;
    } catch (err) {
      if (err instanceof HttpError) {
        const serverError = (err.data.error as string | undefined) ||
                            (err.data.message as string | undefined);
        switch (err.status) {
          case 401:
            throw new Error(
              'Your session has expired. Please reload the page and try again.'
            );
          case 403:
            throw new Error(
              'You are not authorized to disconnect this store. ' +
              'Ensure you are logged in to the correct vendor account.'
            );
          case 404:
            throw new Error(
              'No active Shopify connection was found for your account. ' +
              'The store may already be disconnected.'
            );
          default:
            throw new Error(
              serverError ??
              `Failed to disconnect store (server error ${err.status}). Please try again.`
            );
        }
      }
      throw err; // network failure or other unexpected error - let the caller handle it
    }
  }

  /**
   * Find vendor by shop domain
   */
  async getVendorByShop(domain: string): Promise<{
    vendorId: number;
    shopDomain: string;
  }> {
    const headers = await this.getShopifyAuthHeader();
    const params = new URLSearchParams({ domain });
    const response = await this.get<{
      vendorId: number;
      shopDomain: string;
    }>(`/api-bf/shopify/vendor-by-shop?${params.toString()}`, {
      headers,
    });
    return response.data;
  }

  /**
   * Trigger manual sync for vendor.
   * Returns 202 Accepted immediately - poll getSyncStatus() to track progress.
   */
  async triggerManualSync(vendorId: number): Promise<{ started: boolean; vendorId: number }> {
    const headers = await this.getShopifyAuthHeader();
    const response = await this.post<{ started: boolean; vendorId: number }>(
      `/api-bf/shopify/manual-sync/${vendorId}`,
      {},
      { headers }
    );
    return response.data;
  }

  /**
   * Poll sync status for a vendor.
   * running=true while the background import is in progress.
   * lastResult is null if no import has completed since server start.
   */
  async getSyncStatus(vendorId: number): Promise<{
    running: boolean;
    progress: {
      created: number;
      updated: number;
      skipped: number;
      errors: number;
      imageErrors: number;
      deleted: number;
    } | null;
    lastResult: {
      success: boolean;
      message: string;
      created: number;
      updated: number;
      skipped: number;
      deleted: number;
      imageErrors: number;
      errors: number;
      durationMs: number;
      productErrors: Array<{ title: string; reason: string }>;
    } | null;
  }> {
    const headers = await this.getShopifyAuthHeader();
    const response = await this.get<{
      running: boolean;
      progress: {
        created: number;
        updated: number;
        skipped: number;
        errors: number;
        imageErrors: number;
        deleted: number;
      } | null;
      lastResult: {
        success: boolean;
        message: string;
        created: number;
        updated: number;
        skipped: number;
        deleted: number;
        imageErrors: number;
        errors: number;
        durationMs: number;
        productErrors: Array<{ title: string; reason: string }>;
      } | null;
    }>(`/api-bf/shopify/manual-sync/${vendorId}/status`, { headers });
    return response.data;
  }

  /**
   * Generate a 6-character link code for linking shop to vendor account
   * Uses Shopify session token (from App Bridge) - NO marketplace JWT needed
   * Code expires in 15 minutes and can be entered by vendor in marketplace dashboard
   * Rate limited to 5 codes per hour per shop
   */
  async generateLinkCode(): Promise<{
    success: boolean;
    code?: string;
    expiresIn?: number;
    requiresOAuth?: boolean;
    error?: string;
  }> {
    const headers = await this.getShopifyAuthHeader(); // Gets Shopify session token
    const response = await this.post<{
      success: boolean;
      code?: string;
      expiresIn?: number;
      error?: string;
    }>(
      '/api-bf/shopify/generate-link-code',
      {}, // No body needed - shop domain extracted from session token
      { headers }
    );
    return response.data;
  }

  /**
   * Initiate OAuth re-authorization using session token
   * Uses Shopify session token (from App Bridge) - NO marketplace JWT needed
   * Returns OAuth authorization URL for App Bridge Redirect API
   */
  async initiateOAuthReauthorization(): Promise<{
    success: boolean;
    authorizationUrl?: string;
    error?: string;
  }> {
    const headers = await this.getShopifyAuthHeader(); // Gets Shopify session token
    const response = await this.post<{
      success: boolean;
      authorizationUrl?: string;
      error?: string;
    }>(
      '/api-bf/shopify/initiate-oauth-reauth',
      {}, // No body needed - shop domain extracted from session token
      { headers }
    );
    return response.data;
  }

  /**
   * Initiate OAuth flow for re-authorization (when cached token expired)
   * Requires marketplace JWT authentication (vendor must be logged in)
   * Returns authorization URL to redirect user to Shopify consent screen
   * @param shop - Shopify shop domain (e.g., "store.myshopify.com")
   * @deprecated Use initiateOAuthReauthorization() with session tokens instead
   */
  async initiateOAuth(shop: string): Promise<{
    success: boolean;
    authorizationUrl?: string;
    error?: string;
  }> {
    // Get marketplace auth token from localStorage (not Shopify session token)
    const authToken = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
    
    if (!authToken) {
      throw new Error('Marketplace authentication required. Please log in first.');
    }

    // Validate JWT format before making API call
    if (!this.isValidJwtFormat(authToken)) {
      throw new Error('Session expired or invalid. Please sign in again.');
    }

    const params = new URLSearchParams({ shop });
    const response = await this.get<{
      success: boolean;
      authorizationUrl?: string;
      error?: string;
    }>(
      `/api-bf/shopify/initiate-oauth?${params.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      }
    );
    return response.data;
  }

  /**
   * Link vendor account to Shopify store using email lookup and session token
   * Uses Shopify session token (from App Bridge) - NO marketplace JWT needed
   * @param email - Vendor email address to link to the current shop
   */
  async linkVendorByEmail(email: string): Promise<{
    success: boolean;
    vendorId?: number;
    shopDomain?: string;
    error?: string;
  }> {
    const headers = await this.getShopifyAuthHeader(); // Gets Shopify session token
    const response = await this.post<{
      success: boolean;
      vendorId?: number;
      shopDomain?: string;
      error?: string;
    }>(
      '/api-bf/shopify/link-vendor-by-email',
      { email },
      {
        headers: {
          ...headers, // Shopify session token in Authorization header
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  }

  /**
   * Link vendor account to Shopify store
   * Requires marketplace JWT authentication (vendor must be logged in)
   * @param shop - Shopify shop domain (e.g., "store.myshopify.com")
   */
  async linkVendor(shop: string): Promise<{
    success: boolean;
    vendorId?: number;
    shopDomain?: string;
    error?: string;
  }> {
    // Get marketplace auth token from localStorage (not Shopify session token)
    const authToken = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
    
    if (!authToken) {
      throw new Error('Marketplace authentication required. Please log in first.');
    }

    // Validate JWT format before making API call
    if (!this.isValidJwtFormat(authToken)) {
      throw new Error('Session expired or invalid. Please sign in again.');
    }

    const response = await this.post<{
      success: boolean;
      vendorId?: number;
      shopDomain?: string;
      error?: string;
    }>(
      '/api-bf/shopify/link-vendor',
      { shop },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      }
    );
    return response.data;
  }

  /**
   * Initiate platform-first Shopify connection
   * Vendor starts from marketplace dashboard, enters shop domain
   * Returns OAuth URL with vendorId embedded in state parameter
   * Requires marketplace JWT authentication (vendor must be logged in)
   * @param shopDomain - Shopify shop domain (e.g., "store.myshopify.com")
   */
  async connectShopify(shopDomain: string): Promise<{
    success: boolean;
    authorizationUrl?: string;
    error?: string;
  }> {
    // Get marketplace auth token from localStorage
    const authToken = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
    
    if (!authToken) {
      throw new Error('Marketplace authentication required. Please log in first.');
    }

    // Validate JWT format before making API call
    if (!this.isValidJwtFormat(authToken)) {
      throw new Error('Session expired or invalid. Please sign in again.');
    }

    const response = await this.post<{
      success: boolean;
      authorizationUrl?: string;
      error?: string;
    }>(
      '/api-bf/vendor/connect-shopify',
      { shopDomain },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      }
    );
    return response.data;
  }

  /**
   * Link Shopify store to vendor account using short code
   * Vendor enters 6-character code generated by embedded app
   * Requires marketplace JWT authentication (vendor must be logged in)
   * @param code - 6-character link code from embedded app
   */
  async linkShopifyByCode(code: string): Promise<{
    success: boolean;
    shopDomain?: string;
    error?: string;
  }> {
    // Get marketplace auth token from localStorage
    const authToken = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
    
    if (!authToken) {
      throw new Error('Marketplace authentication required. Please log in first.');
    }

    // Validate JWT format before making API call
    if (!this.isValidJwtFormat(authToken)) {
      throw new Error('Session expired or invalid. Please sign in again.');
    }

    const response = await this.post<{
      success: boolean;
      shopDomain?: string;
      error?: string;
    }>(
      '/api-bf/vendor/link-shopify-by-code',
      { code },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      }
    );
    return response.data;
  }
}

// Base URL for Shopify API endpoints
const SHOPIFY_BASE_URL = 'https://' + import.meta.env.VITE_BACKEND_CUSTOM_DOMAIN_NAME;

// Export singleton instance
export const shopifyApi = new ShopifyApiClient(SHOPIFY_BASE_URL);
